import { Event, EventDispatcher } from "./events";

export class SoundTransform {
  constructor(
    public volume = 1,
    public pan = 0,
  ) {}
}

export function audioReady(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext();
    masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
  }
  if (audioContext.state === "suspended") void audioContext.resume();
  return audioContext;
}

export class SoundChannel extends EventDispatcher {
  constructor(buffer: AudioBuffer | null, startTimeMs: number, loops: number) {
    super();
    if (!buffer) return;
    const ctx = audioReady();
    this.gain = ctx.createGain();
    this.gain.connect(masterGain!);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    if (loops > 0) {
      // looping the decoded buffer is gapless, unlike re-triggering playback.
      source.loop = true;
      source.loopStart = 0;
      source.loopEnd = buffer.duration;
    }
    source.connect(this.gain);
    source.onended = () => {
      if (this.stopped) return;
      this.stopped = true;
      this.dispatchEvent(new Event(Event.SOUND_COMPLETE));
    };
    source.start(0, Math.min(startTimeMs / 1000, buffer.duration));
    this.node = source;
  }

  get soundTransform(): SoundTransform {
    return new SoundTransform(this.gain?.gain.value ?? 1);
  }

  set soundTransform(t: SoundTransform) {
    if (this.gain) this.gain.gain.value = Math.max(0, Math.min(1, t.volume));
  }

  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    try {
      this.node?.stop();
    } catch {
      // already ended
    }
    this.node = null;
  }

  private gain: GainNode | null = null;
  private node: AudioBufferSourceNode | null = null;
  private stopped = false;
}

/** flash.media.Sound. Asset subclasses supply the id via super(). */
export class Sound extends EventDispatcher {
  constructor(assetId?: string) {
    super();
    if (assetId) this.assetId = assetId;
  }

  play(startTime = 0, loops = 0, transform?: SoundTransform): SoundChannel {
    const buffer = this.assetId
      ? (bufferCache.get(this.assetId) ?? null)
      : null;
    const channel = new SoundChannel(buffer, startTime, loops);
    if (transform) channel.soundTransform = transform;
    return channel;
  }

  private assetId: string | null = null;
}

export async function loadSound(
  id: string,
  urls: string[],
): Promise<AudioBuffer | null> {
  const cached = bufferCache.get(id);
  if (cached) return cached;
  const inFlight = pending.get(id);
  if (inFlight) return inFlight;
  const task = (async () => {
    const ctx = audioReady();
    for (const url of urls) {
      try {
        const res = await fetch(url);
        if (!res.ok) continue;
        const bytes = await res.arrayBuffer();
        const buffer = await ctx.decodeAudioData(bytes);
        bufferCache.set(id, buffer);
        return buffer;
      } catch {
        // try the next container
      }
    }
    return null;
  })();
  pending.set(id, task);
  const result = await task;
  pending.delete(id);
  return result;
}

let audioContext: AudioContext | null = null;
let masterGain: GainNode | null = null;

const bufferCache = new Map<string, AudioBuffer>();
const pending = new Map<string, Promise<AudioBuffer | null>>();
